import { defineSlotRecipe } from '@chakra-ui/react';

/*
 * Design system source: .claude/skills/monetizenow-design/preview/cards-kpi.html
 * and Primitives.jsx Card reference.
 *
 * 6px radius (md), 1px border-subtle, xs shadow. Body padding 16px (space-4).
 *
 * v3 port notes:
 * - Slots renamed from v2's `cardAnatomy`: `container` → `root`. `header`/
 *   `body`/`footer` keep their names; v3 adds `title`/`description` slots
 *   this app doesn't use anywhere (no MCardTitle/MCardDescription wrapper).
 * - Chakra's built-in `card` recipe defaults to `variant: 'outline'` and
 *   deep-merges *under* this one (same mechanism as Checkbox.ts/Switch.ts),
 *   and its base also sets flex/gap/padding on header/body/footer. Every
 *   slot below explicitly restates every key the built-in sets so none of
 *   that leaks onto call sites that pass no variant — confirmed by resolving
 *   the merged recipe directly against a throwaway `createSystem` (not just
 *   reading the built-in's source).
 * - Padding matches what v2 actually rendered: v2's `header: {}` /
 *   `footer: {}` here were empty, but merged over Chakra's own v2 card theme,
 *   which padded both with `--card-padding` (1.25rem at the default `md`
 *   size). Body was overridden to a flat `padding: '4'`.
 * - `root`'s own `display: flex; flexDirection: column` from the built-in is
 *   kept (not neutralized) — that's genuinely structural (Card's children
 *   need to stack), not a v2/v3 behavioral difference.
 * - `editable` variant: `--card-border-width`/`--card-border-color` are
 *   carried over as literal custom properties (v2 used `cssVar()` from the
 *   removed `@chakra-ui/theme-tools` to the same effect) even though
 *   nothing in the app currently reads them or passes `variant="editable"`
 *   — ported mechanically rather than dropped, consistent with "port, not
 *   redesign."
 */

// v2 header/footer padding (`--card-padding` at v2's default `md` size),
// restated in full so the built-in's `paddingInline`/`paddingTop`/
// `paddingBottom` and flex layout don't leak through. `flexDirection` and
// `alignItems` only matter once a call site sets `display="flex"`
// (PreviewPdfCard's header), where the built-in `column`/`center` would
// otherwise stack the children instead of v2's row.
const headerFooterBase = {
  display: 'block',
  flexDirection: 'row',
  alignItems: 'normal',
  paddingInline: 5,
  paddingTop: 5,
  paddingBottom: 5,
  gap: 0,
};

export const cardTheme = defineSlotRecipe({
  slots: ['root', 'header', 'body', 'footer', 'title', 'description'],
  base: {
    root: {
      borderRadius: 'md', // 6px
      backgroundColor: 'bg.surface',
      boxShadow: 'xs',
    },
    header: headerFooterBase,
    body: {
      padding: '4',
      display: 'block',
      flexDirection: 'row',
    },
    footer: headerFooterBase,
  },
  variants: {
    variant: {
      // Redefines the built-in `outline` variant — this recipe's default, as
      // v2's `defaultProps.variant: 'outline'` was. This app's v2 theme had no
      // `outline` of its own, but merged over Chakra's v2 card theme, whose
      // `outline` set `--card-border-width: 1px` and `--card-border-color:
      // chakra-border-color` (gray.200, #E2E8F0) for the base container's
      // `borderWidth`/`borderColor` — so a card with no variant had a 1px
      // border, recolored by call-site `borderColor`. `border.subtle` is the
      // design system's equivalent.
      outline: {
        root: {
          bg: 'bg.surface',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.subtle',
        },
      },
      borderless: {
        root: {
          boxShadow: 'none',
        },
      },
      border: {
        root: {
          borderColor: 'border.subtle',
          borderRadius: 'md',
          borderWidth: '1px',
          borderStyle: 'solid',
          boxShadow: 'xs',
        },
      },
      edit: {
        root: {
          backgroundColor: 'bg.surface',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.subtle',
          borderRadius: 'md',
          boxShadow: 'xs',
        },
        header: {
          py: 3,
          px: 4,
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderBottomColor: 'border.subtle',
          fontSize: 'sm',
          fontWeight: 500,
          color: 'fg.strong',
        },
        body: {
          px: 4,
          py: 4,
        },
        footer: {
          px: 4,
          py: 3,
          borderTopWidth: '1px',
          borderTopStyle: 'solid',
          borderTopColor: 'border.subtle',
        },
      },
      editable: {
        root: {
          '--card-border-width': '1px',
          '--card-border-color': 'colors.chakra-border-color',
          _disabled: {
            borderColor: 'inherit !important',
            cursor: 'default !important',
            boxShadow: 'none !important',
            userSelect: 'text !important',
          },
          _hover: {
            boxShadow: 'sm',
            cursor: 'pointer',
            borderColor: 'border.accent',
          },
          _focusVisible: {
            boxShadow: 'sm',
            cursor: 'pointer',
            borderColor: 'border.accent',
          },
        },
      },
    },
  },
  defaultVariants: {
    variant: 'outline',
  },
});

export default cardTheme;
